import { TextField, TextFieldProps } from '@mui/material';
import { useField } from 'formik';
import { isNil, isNumber } from 'lodash';
import React, { useCallback } from 'react';
import {
  NumericFormat,
  NumberFormatValues,
  NumericFormatProps,
} from 'react-number-format';

import { ROUND_DECIMALS } from 'constants/common.constants';
import { useFormikError } from 'hooks';

type Props = {
  name: string;
  label: string;
  percentageSuffix?: boolean;
  defaultValue?: number;
  thousandSeparator?: string;
  onChangeHandler?: (value: string) => void;
} & Partial<NumericFormatProps> &
  Partial<TextFieldProps>;

// Component is mostly the same as FormikNumericField but works with string values (required for BigInt)
export const FormikNumericTextField: React.FC<Props> = ({
  name,
  percentageSuffix,
  suffix,
  defaultValue,
  thousandSeparator = ' ',
  onChangeHandler,
  ...rest
}) => {
  const [field, , helpers] = useField(name);

  const { errorVisible, errorMessage } = useFormikError(name);

  const handleChange = useCallback(
    ({ value }: NumberFormatValues) => {
      const numericValue = Number(value);
      if (String(numericValue) !== value) {
        value = '';
      }
      const isValidValue = isNumber(numericValue) && !isNaN(numericValue);
      let stringValue = isValidValue ? String(value) : '';
      if (isValidValue) {
        onChangeHandler?.(stringValue);
      }
      helpers.setValue(
        isValidValue
          ? stringValue
          : rest.required && isNil(value)
          ? ''
          : defaultValue,
      );
    },
    [helpers, defaultValue, rest.required, onChangeHandler],
  );

  const handleBlur = useCallback(() => helpers.setTouched(true), [helpers]);

  return (
    <NumericFormat
      decimalScale={ROUND_DECIMALS}
      {...rest}
      name={name}
      value={field.value}
      onValueChange={handleChange}
      customInput={TextField}
      decimalSeparator=","
      thousandSeparator={thousandSeparator}
      error={errorVisible}
      helperText={errorMessage || rest.helperText}
      size="small"
      type="text"
      suffix={percentageSuffix ? ' %' : suffix ? ` ${suffix}` : ''}
      onBlur={handleBlur}
    />
  );
};
